import {
  Drawer,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useIntegrationState } from "./hooks/useIntegrationState";
import JiraConfig from "./platforms/JiraConfig";
import AzureConfig from "./platforms/AzureConfig";
import SlackTeamsConfig from "./platforms/SlackTeamsConfig";
import AsanaConfig from "./platforms/AsanaConfig";
import type { IntegrationConfigDrawerProps } from "./types";

export default function IntegrationConfigDrawer({
  open,
  onClose,
  projectDetails,
  platform,
}: IntegrationConfigDrawerProps) {
  const { t } = useTranslation(["bugherd"]);
  const navigate = useNavigate();

  const {
    state,
    setState,
    isJiraConfigured,
    isAzureConfigured,
    configData,
    isLoadingConfig,
    handleSave,
    isSaving,
    // Jira
    handleProjectChange,
    updateFieldConfig,
    handleDefaultFieldChange,
    // Azure
    handleAzureProjectChange,
    updateAzureFieldConfig,
    handleAzureOrgChange,
    // Communication
    handleWebhookChange,
  } = useIntegrationState(projectDetails, platform, open);

  const navigateToCompanySettings = () => {
    onClose();
    navigate("/company-settings/settings/integration");
  };

  const getSubtitle = () => {
    switch (platform) {
      case "AZURE":
        return t("Azure Boards Integration");
      case "JIRA":
        return t("Jira Integration");
      case "ASANA":
        return t("Asana Integration");
      case "SLACK":
        return t("Slack Configuration");
      case "TEAMS":
        return t("Microsoft Teams Configuration");
      default:
        return t("Integration Configuration");
    }
  };

  const onSave = async () => {
    const success = await handleSave();
    if (success) {
      onClose();
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 470,
          minWidth: 380,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : "#f8f9fa",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: (theme) => theme.palette.background.paper,
            flexShrink: 0,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                background: (theme) => `${theme.palette.primary.main}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckIcon
                sx={{
                  fontSize: 20,
                  color: (theme) => theme.palette.primary.main,
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} fontSize="15px">
                {t("Configuration")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize="11.5px"
              >
                {getSubtitle()}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              sx={{
                minWidth: "90px",
                borderRadius: "7px",
                textTransform: "none",
                fontWeight: 500,
              }}
              onClick={onClose}
            >
              {t("Cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              onClick={onSave}
              loading={isSaving}
              sx={{
                minWidth: "90px",
                borderRadius: "7px",
                textTransform: "none",
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {t("Save")}
            </LoadingButton>
          </Box>
        </Box>

        {/* Content */}
        {!isLoadingConfig ? (
          <Box
            sx={{
              p: 3,
              flex: 1,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.15)",
                borderRadius: "3px",
              },
            }}
          >
            {platform === "JIRA" && (
              <JiraConfig
                projectDetails={projectDetails}
                state={state}
                isJiraConfigured={isJiraConfigured}
                handleProjectChange={handleProjectChange}
                updateFieldConfig={updateFieldConfig}
                handleDefaultFieldChange={handleDefaultFieldChange}
                onNavigateToCompanySettings={navigateToCompanySettings}
              />
            )}

            {platform === "AZURE" && (
              <AzureConfig
                projectDetails={projectDetails}
                state={state}
                isAzureConfigured={isAzureConfigured}
                handleAzureProjectChange={handleAzureProjectChange}
                updateAzureFieldConfig={updateAzureFieldConfig}
                handleAzureOrgChange={handleAzureOrgChange}
                onNavigateToCompanySettings={navigateToCompanySettings}
              />
            )}

            {(platform === "SLACK" || platform === "TEAMS") && (
              <SlackTeamsConfig
                platform={platform}
                projectDetails={projectDetails}
                state={state}
                configData={configData}
                handleWebhookChange={handleWebhookChange}
                onNavigateToCompanySettings={navigateToCompanySettings}
              />
            )}

            {platform === "ASANA" && <AsanaConfig />}
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
            p={4}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
